import { codeboxApi } from "api/codebox";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "providers/Layout";
import { useAuthContext } from "providers/Auth";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import { Card } from "shared/components/Card";
import { Download01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { IntegrationDetailHeader } from "views/settings-integration/integrations/shared/IntegrationDetailHeader";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { ImportLockboxesModal } from "./components/ImportLockboxesModal";

type CodeboxTab = "Credentials";

const CODEBOX_TABS: Tab<CodeboxTab>[] = [
  { value: "Credentials", label: "Credentials" }
];

export const CodeboxView = () => {
  const navigate = useNavigate();
  const { refreshPartnerData } = useAuthContext();
  const queryClient = useQueryClient();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState<CodeboxTab>("Credentials");
  const [importModalOpen, setImportModalOpen] = useState(false);

  const handleImportSuccess = useCallback(() => {
    setImportModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ["LOCKBOXES"] });
    navigate("/settings/integration");
  }, [queryClient, navigate]);

  const { mutate: disconnectCodebox, isPending: isDisconnecting } = useMutation(
    {
      mutationKey: ["CODEBOX", "DELETE"],
      mutationFn: () => codeboxApi.disconnect(),
      onSuccess: () => {
        toast.success("Codebox disconnected successfully");
        refreshPartnerData();
        navigate("/settings/integration");
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    }
  );

  const { mutate: updateCodebox, isPending } = useMutation({
    mutationKey: ["CODEBOX", "UPDATE"],
    mutationFn: () => codeboxApi.connect({ username, password }),
    onSuccess: () => {
      toast.success("Codebox credentials updated successfully");
      refreshPartnerData();
      navigate("/settings/integration");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Layout
      header={
        <IntegrationDetailHeader
          id={IntegrationId.CODEBOX}
          name="Codebox"
          path="/settings/integration/codebox"
          onDisconnect={() => disconnectCodebox()}
          isDisconnecting={isDisconnecting}
          actions={
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              HugeIcon={Download01Icon}
              onClick={() => setImportModalOpen(true)}
            >
              Import lockboxes
            </Button>
          }
        />
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.BASE}
            onClick={() => navigate("/settings/integration")}
            className="w-[125px]"
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            onClick={() => updateCodebox()}
            className="w-[125px]"
            disabled={!username || !password}
            loading={isPending}
          >
            Save
          </Button>
        </div>
      }
    >
      <Tabs
        tabs={CODEBOX_TABS}
        active={activeTab}
        onChange={setActiveTab}
        tabStyle="line"
        size="md"
        fullWidthTabs={false}
      />
      <Card className="w-full p-6 mt-4">
        <p className="text-base font-bold text-content-1000">Credentials</p>
        <div className="mt-6 border-t border-alpha-40 pt-6 space-y-4">
          <BaseInput
            label="Username"
            required
            id="codebox-username"
            placeholder="Username"
            value={username}
            onChange={setUsername}
          />
          <BaseInput
            label="Password"
            required
            id="codebox-password"
            type="password"
            placeholder="**********************"
            value={password}
            onChange={setPassword}
          />
        </div>
      </Card>
      <ImportLockboxesModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </Layout>
  );
};
